import React, { SetStateAction, useEffect, useState } from "react";
import { SelectOption } from "@avamae/formbuilder/dist/FormSingleSelectField";
import ApprovalDecision, { EditApprovalDecision } from "./ApprovalDecision";
import _ from "lodash";
import Action from "./Action";
import {
  ParsedWorkflowItem,
  ReferenceData,
  UnParsedTasksAndMilestones,
  WorkflowActionTypes,
  WorkflowBuilderErrors,
  WorkflowReferenceDataNames,
} from "../WorkflowBuilderPage";
import TriggerTypeSelector from "./TriggerTypeSelector";
import DocumentUploadTrigger from "./DocumentUploadTrigger";
import DocumentRequestTrigger from "./DocumentRequestTrigger";
import MilestoneUpdateTrigger from "./MilestoneUpdateTrigger";
import { Dropdown } from "components/dropdown/Dropdown";
import { List } from "./MilestoneTaskPicker";
import SubmittedSurveyTrigger from "./SubmittedSurveyTrigger";
import NewCommunicationTrigger from "./NewCommunicationTrigger";
import { DispatchedSurveyTrigger } from "./DispatchedSurveyTrigger";

export const getAllOrderNumbers = (parsedData: any, referenceData: any) => {
  let allOrderNumbers = checkExistingValueIds(parsedData, "orderNumber");

  if (referenceData) {
    Object.keys(referenceData).forEach((key: string) => {
      const value = referenceData[key as keyof typeof referenceData];

      const tempOrderNumbers = checkExistingValueIds(value, "orderNumber");

      allOrderNumbers = [...allOrderNumbers, ...tempOrderNumbers];
    });
  }

  return allOrderNumbers;
};

type ChildElementIdsToDelete = {
  actionIds: number[];
  approvalIds: number[];
  approvalOutcomeIds: number[];
};

const getExtraActionIdsToDelete = (
  referenceData: any,
  idToCheck: number,
  keyToCheck: string,
  idsToDelete: number[]
) => {
  let tempIdsToDelete = [...idsToDelete];

  if (referenceData && referenceData.actions && Array.isArray(referenceData.actions)) {
    const match = referenceData.actions.filter((item: any) => {
      if (
        typeof item === "object" &&
        item.hasOwnProperty(keyToCheck) &&
        item[keyToCheck] === idToCheck
      ) {
        return true;
      } else {
        return false;
      }
    });

    if (
      match.length === 1 &&
      typeof match[0] === "object" &&
      match[0].hasOwnProperty("orderNumber") &&
      match[0].hasOwnProperty("workflowActionsID")
    ) {
      const matchOrderNumber = match[0].orderNumber;

      tempIdsToDelete.push(match[0].workflowActionsID);

      const actionParentOrderNumbers = referenceData.actions.map(
        (item: any) => item.parentOrderNumber
      );

      if (actionParentOrderNumbers.includes(matchOrderNumber)) {
        tempIdsToDelete = getExtraActionIdsToDelete(
          referenceData,
          matchOrderNumber,
          "parentOrderNumber",
          tempIdsToDelete
        );
      }
    }
  }

  return tempIdsToDelete;
};

const replaceObjectValue = (
  obj: any,
  keyToCheck: string,
  newValue: any,
  replaceOrAppend: "replace" | "append"
) => {
  let data: any = { ...obj };

  if (typeof obj === "object" && obj.hasOwnProperty(keyToCheck)) {
    if (replaceOrAppend === "replace") {
      data[keyToCheck] = newValue;
    } else {
      if (Array.isArray(data[keyToCheck])) {
        data[keyToCheck] = [...data[keyToCheck], newValue];
      } else {
        data[keyToCheck] = [newValue];
      }
    }
  } else {
    if (
      typeof obj === "object" &&
      obj.hasOwnProperty("childElements") &&
      Array.isArray(obj.childElements)
    ) {
      obj.childElements.forEach((item: any, idx: number) => {
        if (typeof item === "object" && item.hasOwnProperty(keyToCheck)) {
          data.childElements[idx] = replaceObjectValue(item, keyToCheck, newValue, replaceOrAppend);
        }
      });
    }
  }

  return data;
};

export const addWorkflowBuilderReferenceData = (
  referenceData: any,
  type: WorkflowReferenceDataNames,
  valueToAdd: any
) => {
  let newReferenceData = _.cloneDeep(referenceData);

  if (referenceData && typeof referenceData === "object" && referenceData.hasOwnProperty(type)) {
    if (Array.isArray(referenceData[type])) {
      newReferenceData[type] = [...referenceData[type], valueToAdd];
    } else {
      newReferenceData[type] = [valueToAdd];
    }
  }

  return newReferenceData;
};

export const getAllItemActions = (
  referenceData: ReferenceData,
  keyToCheck: string,
  idTocheck: number
): any => {
  let actions = [];

  let newIdToCheck = idTocheck;

  if (referenceData && referenceData.actions) {
    const match = referenceData.actions.filter((item: any) => {
      if (item.hasOwnProperty(keyToCheck) && item[keyToCheck] === newIdToCheck) {
        return true;
      } else {
        return false;
      }
    });

    if (match.length === 1) {
      actions.push(match[0]);

      if (match[0].hasOwnProperty("orderNumber")) {
        newIdToCheck = match[0].orderNumber;

        const tempActions = getAllItemActions(referenceData, "parentOrderNumber", newIdToCheck);

        if (Array.isArray(tempActions)) {
          actions = [...actions, ...tempActions];
        }
      }
    }
  }
  return actions;
};

export const changeWorkflowBuilderReferenceData = (
  referenceData: any,
  type: WorkflowReferenceDataNames,
  idKey: string | null,
  idValue: number | null,
  keyToChange: string,
  valueToChange: any,
  operation: "replace" | "append" | "delete" | "create" = "replace"
) => {
  let newReferenceData = _.cloneDeep(referenceData);

  if (
    referenceData &&
    typeof referenceData === "object" &&
    referenceData.hasOwnProperty(type) &&
    Array.isArray(referenceData[type])
  ) {
    if (idKey && idValue) {
      const match = referenceData[type].filter((item: any) => item[idKey] === idValue);

      if (match.length === 1) {
        let data = match[0];

        let newTypeData = [...referenceData[type]];

        if (operation === "delete") {
          const filteredData = newTypeData.filter((item: any) => {
            if (typeof item === "object" && item.hasOwnProperty(idKey) && item[idKey] === idValue) {
              return false;
            } else {
              return true;
            }
          });

          newTypeData = filteredData;
        } else {
          if (typeof data == "object" && data.hasOwnProperty(keyToChange)) {
            if (operation === "replace") {
              data[keyToChange] = valueToChange;
            } else if (operation === "append" && Array.isArray(data[keyToChange])) {
              data[keyToChange] = [...data[keyToChange], valueToChange];
            } else if (operation === "append" && !Array.isArray(data[keyToChange])) {
              data[keyToChange] = [valueToChange];
            }

            const index = referenceData[type].findIndex((item: any) => item[idKey] === idValue);

            newTypeData[index] = data;
          } else if (
            operation === "create" &&
            typeof data == "object" &&
            !data.hasOwnProperty(keyToChange)
          ) {
            data[keyToChange] = valueToChange;

            const index = referenceData[type].findIndex((item: any) => item[idKey] === idValue);

            newTypeData[index] = data;
          }
        }

        newReferenceData[type] = newTypeData;
      }
    } else if (!idKey && !idValue && operation === "append") {
      // For appending new values to reference data type arrays
      let newTypeData = [...referenceData[type]];
      newTypeData.push(valueToChange);

      newReferenceData[type] = newTypeData;
    }
  } else {
    // For trigger which is an object:

    if (type === WorkflowReferenceDataNames.TRIGGER) {
      let data = newReferenceData[type];

      if (typeof data == "object" && data.hasOwnProperty(keyToChange)) {
        if (operation === "replace") {
          data[keyToChange] = valueToChange;
        } else if (operation === "append" && Array.isArray(data[keyToChange])) {
          data[keyToChange] = [...data[keyToChange], valueToChange];
        } else if (operation === "append" && !Array.isArray(data[keyToChange])) {
          data[keyToChange] = [valueToChange];
        }
      }

      newReferenceData[type] = data;
    }
  }

  return newReferenceData;
};

export const updateWorkflowBuilderValue = (
  parsedData: any,
  unqiueId: string,
  keyToUpdate: string,
  valueToUpdate: any,
  replaceOrAppend: "replace" | "append" = "replace"
) => {
  let splitId = unqiueId.split(",").map((item) => parseInt(item.trim()));

  let parsedId: string[] = [];

  splitId.map((item, idx) => {
    parsedId.push(`[${item}]`);

    if (idx !== splitId.length - 1) {
      parsedId.push(`[childElements]`);
    }
  });

  parsedId.push(`[${keyToUpdate}]`);

  const finalId = parsedId.join("");

  let newParsedData = _.cloneDeep(parsedData);

  let tempExistingValueId = parsedId.slice(1);

  const existingValueId = `${tempExistingValueId.join("")}`;

  if (replaceOrAppend === "append" && parsedId && Array.isArray(parsedId)) {
    const existingValue = _.get(parsedData, existingValueId);

    let newValue: any = null;
    if (Array.isArray(existingValue)) {
      newValue = [...existingValue, valueToUpdate];
    } else {
      newValue = [valueToUpdate];
    }

    _.set(newParsedData, existingValueId, newValue);
  } else {
    _.set(newParsedData, existingValueId, valueToUpdate);
  }

  return newParsedData;
};

const checkObjectForValue = (obj: any, keyToCheck: string) => {
  let ids: number[] = [];

  if (obj && typeof obj === "object" && obj.hasOwnProperty(keyToCheck)) {
    ids.push(obj[keyToCheck]);
  }

  if (
    obj &&
    typeof obj === "object" &&
    obj.hasOwnProperty("childElements") &&
    Array.isArray(obj.childElements)
  ) {
    obj.childElements.forEach((item: any) => {
      const newIds = checkObjectForValue(item, keyToCheck);

      if (Array.isArray(newIds)) {
        ids = [...ids, ...newIds];
      }
    });
  }

  return ids;
};

export const getMaxNumberInArray = (arr: any) => {
  if (Array.isArray(arr)) {
    return Math.max(...arr.filter((item: any) => typeof item === "number").sort((a, b) => a - b));
  } else {
    return null;
  }
};

export const checkExistingValueIds = (data: any, keyToCheck: string) => {
  let ids: any = [];

  if (Array.isArray(data)) {
    data.forEach((item: any) => {
      const tempIds = checkObjectForValue(item, keyToCheck);

      ids = [...ids, ...tempIds];
    });
  } else {
    const tempIds = checkObjectForValue(data, keyToCheck);

    ids = [...ids, ...tempIds];
  }

  return ids;
};

export const checkExistingReferenceDataValueIds = (
  data: any,
  typeKey: WorkflowReferenceDataNames,
  keyToCheck: string
) => {
  let ids: number[] = [];
  if (typeof data === "object" && data.hasOwnProperty(typeKey) && Array.isArray(data[typeKey])) {
    data[typeKey].forEach((item: any) => {
      if (item.hasOwnProperty(keyToCheck) && typeof item[keyToCheck] === "number") {
        ids.push(item[keyToCheck]);
      }
    });
  }
  return ids;
};

export const getExistingValueFromParsedData = (
  parsedData: any,
  uniqueId: string,
  keyToCheck: string
) => {
  let splitId = uniqueId.split(",").map((item) => parseInt(item.trim()));

  let parsedId: string[] = [];

  splitId.map((item, idx) => {
    parsedId.push(`[${item}]`);

    if (idx !== splitId.length - 1) {
      parsedId.push(`[childElements]`);
    }
  });

  parsedId.push(`[${keyToCheck}]`);

  const finalId = parsedId.join("");

  let newParsedData = _.cloneDeep(parsedData);

  let tempExistingValueId = parsedId.slice(1);

  const existingValueId = `${tempExistingValueId.join("")}`;

  return _.get(newParsedData, existingValueId);
};

export enum NodeTypes {
  "ACTION" = "action",
  "APPROVAL" = "approval",
}

type Props = {
  parentOrderNumbers: string;
  uniqueId: string;
  parentOrderNumber: number;
  orderNumber: number;
  childElements: ParsedWorkflowItem[] | null;
  triggerOptions: SelectOption[];
  documentOptions: SelectOption[];
  projectOptions: SelectOption[];
  triggerDatesOptions: SelectOption[];
  parsedData: ParsedWorkflowItem | null;
  setParsedData: React.Dispatch<SetStateAction<ParsedWorkflowItem | null>>;
  itemData: any;
  referenceData: ReferenceData | null;
  setReferenceData: React.Dispatch<SetStateAction<ReferenceData | null>>;
  contactTypes: SelectOption[];
  userPools: SelectOption[];
  actionTypes: WorkflowActionTypes[];
  parentChildElementsLength: number;
  tempTriggerMilestoneId: number | null;
  setTempTriggerMilestoneId: React.Dispatch<SetStateAction<number | null>>;
  tempTriggerMilestoneData: List | null;
  setTempTriggerMilestoneData: React.Dispatch<SetStateAction<List | null>>;
  tempActionMilestoneId: number | null;
  setTempActionMilestoneId: React.Dispatch<SetStateAction<number | null>>;
  tempActionMilestoneData: List | null;
  setTempActionMilestoneData: React.Dispatch<SetStateAction<List | null>>;
  tempActionDestinationMilestoneId: number | null;
  setTempActionDestinationMilestoneId: React.Dispatch<SetStateAction<number | null>>;
  milestonestasksList: List[] | null;
  setMilestonestasksList: React.Dispatch<SetStateAction<List[] | null>>;
  workflowBuilderErrors: WorkflowBuilderErrors;
  setWorkflowBuilderErrors: React.Dispatch<SetStateAction<WorkflowBuilderErrors>>;
  childElementIndex: number;
  totalNumberofChildElements: number;
  unParsedTasksAndMilestones: UnParsedTasksAndMilestones | null;
  workflows: SelectOption[];
  surveyTemplateOptions: SelectOption[];
};
const WorkflowItem: React.FC<Props> = ({
  parentOrderNumbers,
  uniqueId,
  parentOrderNumber,
  orderNumber,
  childElements,
  triggerOptions,
  documentOptions,
  projectOptions,
  triggerDatesOptions,
  parsedData,
  setParsedData,
  itemData,
  referenceData,
  setReferenceData,
  contactTypes,
  userPools,
  actionTypes,
  tempTriggerMilestoneId,
  setTempTriggerMilestoneId,
  tempTriggerMilestoneData,
  setTempTriggerMilestoneData,
  tempActionMilestoneId,
  setTempActionMilestoneId,
  tempActionMilestoneData,
  setTempActionMilestoneData,
  tempActionDestinationMilestoneId,
  setTempActionDestinationMilestoneId,
  milestonestasksList,
  setMilestonestasksList,
  parentChildElementsLength,
  workflowBuilderErrors,
  setWorkflowBuilderErrors,
  childElementIndex,
  totalNumberofChildElements,
  unParsedTasksAndMilestones,
  workflows,
  surveyTemplateOptions,
}) => {
  const [showTriggerTypeSelector, setShowTriggerTypeSelector] = useState<boolean>(false);

  const [approvalOutcomeErrors, setApprovalOutcomeErrors] = useState<string[] | null>(null);

  const [showApprovalEditBox, setShowApprovalEditBox] = useState<boolean>(false);

  const horizontalLine = childElements && childElements.length > 1;

  const [actions, setActions] = useState<any | null>(null);
  const [showAddNewElement, setShowAddNewElement] = useState<boolean>(true);
  const [showNodeTypeSelector, setShowNodeTypeSelector] = useState<boolean>(false);
  const [nodeType, setNodeType] = useState<number | null>(null);
  const defaultNodeTypes = [
    {
      label: "Action",
      value: 1,
    },
    { label: "Approval", value: 2 },
  ];
  const [nodeTypes, setNodeTypes] = useState<SelectOption[]>(defaultNodeTypes);

  useEffect(() => {
    if (itemData.nodeApprovalId) {
      setShowAddNewElement(false);

      setNodeTypes(defaultNodeTypes.filter((item) => item.value !== 2));
    } else {
      setNodeTypes(defaultNodeTypes);
    }

    if (referenceData && referenceData.trigger && referenceData.trigger.workflowTriggerTypesID) {
      setShowTriggerTypeSelector(false);
    } else {
      setShowTriggerTypeSelector(true);
    }

    if (itemData.nodeFirstActionId) {
      let tempActions: any[] | null = null;

      if (referenceData) {
        tempActions = getAllItemActions(
          referenceData,
          "workflowActionsID",
          itemData.nodeFirstActionId
        );
      }

      setActions(tempActions);
    } else {
      setActions(null);
    }
  }, [itemData, referenceData, showNodeTypeSelector]);

  const triggerTypeId = referenceData?.trigger?.workflowTriggerTypesID
    ? referenceData?.trigger?.workflowTriggerTypesID
    : null;

  const [triggerTypeLabel, setTriggerTypeLabel] = useState<string | null>(null);

  useEffect(() => {
    if (
      typeof triggerTypeId === "number" &&
      triggerOptions &&
      Array.isArray(triggerOptions) &&
      triggerOptions.length > 0 &&
      typeof triggerOptions[0] === "object"
    ) {
      const match = triggerOptions.filter((item) => item.value === triggerTypeId);

      if (match.length === 1 && typeof match[0].label === "string") {
        setTriggerTypeLabel(match[0].label.toLowerCase().trim());
      }
    }
  }, [triggerTypeId, triggerOptions]);

  const handleChangeNodeType = (option: SelectOption) => {
    setNodeType(option.value);
  };

  const addAction = () => {
    const allOrderNumbers = getAllOrderNumbers(parsedData, referenceData);

    const maxOrderNumber = getMaxNumberInArray(allOrderNumbers);

    const newOrderNumber =
      typeof maxOrderNumber === "number" && isFinite(maxOrderNumber) ? maxOrderNumber + 1 : 1;

    let allWorkflowActionIds = checkExistingValueIds(parsedData, "workflowActionsID");

    if (referenceData && referenceData.actions) {
      allWorkflowActionIds = [
        ...allWorkflowActionIds,
        ...checkExistingValueIds(referenceData.actions, "workflowActionsID"),
      ];
    }

    const maxWorkflowActionId = getMaxNumberInArray(allWorkflowActionIds);

    const newWorkflowActionId =
      typeof maxWorkflowActionId === "number" && isFinite(maxWorkflowActionId)
        ? maxWorkflowActionId + 1
        : 1;

    if (typeof newWorkflowActionId === "number" && typeof newOrderNumber === "number") {
      let newParsedData = _.cloneDeep(parsedData);

      let actionParentOrderNumber: number | null =
        typeof itemData.orderNumber === "number"
          ? itemData.orderNumber
          : typeof itemData.parentOrderNumber === "number"
            ? itemData.parentOrderNumber
            : null;

      let newTriggerchildWorkflowActionId: number | null = null;

      if (actions && actions.length > 0) {
        actionParentOrderNumber = actions[actions.length - 1].orderNumber;
      }

      if (!itemData.workflowActionsID) {
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "workflowActionsID",
          newWorkflowActionId,
          "replace"
        );
      }

      if (!itemData.nodeFirstActionId) {
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "nodeFirstActionId",
          newWorkflowActionId,
          "replace"
        );
      }

      if (typeof itemData.orderNumber !== "number") {
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "orderNumber",
          newOrderNumber,
          "replace"
        );
      }

      let newReferenceData = _.cloneDeep(referenceData);

      const blankAction = {
        parentOrderNumber: actionParentOrderNumber,
        orderNumber: newOrderNumber,
        bBusinessDaysOnly: false,
        bExcludeHolidays: false,
        bOverwriteTask: false,
        bEmailNotifications: false,
        baseOnTasksID: null,

        childWorkflowActionID: null,
        childWorkflowApprovalID: null,
        contactTypesIDs: null,
        documentTypesID: null,
        messageText: null,
        taskDayChange: null,
        updateTasksID: null,

        userPoolsID: null,
        workflowActionTypesID: null,
        workflowActionsID: newWorkflowActionId,
        updateDate: null,
        baseOnDate: null,
        bNewlyCreated: true, // So we can delete the id we assign here just before we post to the endpoint
        workflowToTriggersID: true, // So we can delete the id we assign here just before we post to the endpoint
      };

      newReferenceData = changeWorkflowBuilderReferenceData(
        newReferenceData,
        WorkflowReferenceDataNames.ACTIONS,
        null,
        null,
        "workflowActionsID",
        blankAction,
        "append"
      );

      // If an approval exists, we need to change its parentOrderNumber to the order number of the newly added action

      if (itemData.nodeApprovalId) {
        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.APPROVALS,
          "workflowApprovalsID",
          itemData.nodeApprovalId ?? null,

          "parentOrderNumber",
          newOrderNumber,
          "replace"
        );
      }

      // Update approval outcome with the new action id if there is only 1 action:

      if (itemData && !itemData.nodeFirstActionId) {
        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
          "workflowApprovalOutcomesID",
          itemData.workflowApprovalOutcomesID ?? null,
          "childWorkflowActionID",
          newWorkflowActionId,
          "replace"
        );

        // Update triggger:
        if (newReferenceData?.trigger && itemData.parentOrderNumber === -1) {
          newReferenceData.trigger.childWorkflowActionID = newWorkflowActionId;
          newReferenceData.trigger.childWorkflowApprovalID = null;
        }
      }

      // Clear child approval id from outcome:
      newReferenceData = changeWorkflowBuilderReferenceData(
        newReferenceData,
        WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
        "workflowApprovalOutcomesID",
        itemData.workflowApprovalOutcomesID ?? null,
        "childWorkflowApprovalID",
        null,
        "replace"
      );

      setParsedData(newParsedData);
      setReferenceData(newReferenceData);
    }
  };

  const addApproval = () => {
    const allOrderNumbers = getAllOrderNumbers(parsedData, referenceData);

    const maxOrderNumber = getMaxNumberInArray(allOrderNumbers);

    const newOrderNumber =
      typeof maxOrderNumber === "number" && isFinite(maxOrderNumber) ? maxOrderNumber + 1 : 1;

    let allWorkflowApprovalIds = checkExistingValueIds(parsedData, "workflowApprovalsID");

    allWorkflowApprovalIds = checkExistingValueIds(parsedData, "nodeApprovalId");

    allWorkflowApprovalIds = [
      ...allWorkflowApprovalIds,
      ...checkExistingValueIds(parsedData, "childWorkflowApprovalID"),
    ];

    const maxWorkflowApprovalId = getMaxNumberInArray(allWorkflowApprovalIds);

    const newWorkflowApprovalId =
      typeof maxWorkflowApprovalId === "number" && isFinite(maxWorkflowApprovalId)
        ? maxWorkflowApprovalId + 1
        : 1;

    if (typeof newWorkflowApprovalId === "number" && typeof newOrderNumber === "number") {
      let newParsedData = _.cloneDeep(parsedData);

      let approvalParentOrderNumber: number | null =
        typeof itemData.orderNumber === "number"
          ? itemData.orderNumber
          : typeof itemData.parentOrderNumber === "number"
            ? itemData.parentOrderNumber
            : null;

      if (actions && actions.length > 0) {
        approvalParentOrderNumber = actions[actions.length - 1].orderNumber;
      }

      if (!actions || (actions && actions.length === 0)) {
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "childWorkflowApprovalID",
          newWorkflowApprovalId,
          "replace"
        );
      }
      newParsedData = updateWorkflowBuilderValue(
        newParsedData,
        `${uniqueId}`,
        "nodeApprovalId",
        newWorkflowApprovalId,
        "replace"
      );

      if (typeof itemData.orderNumber !== "number") {
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "orderNumber",
          newOrderNumber,
          "replace"
        );
      }

      let newReferenceData = _.cloneDeep(referenceData);

      const blankApproval = {
        parentOrderNumber: approvalParentOrderNumber,
        orderNumber: newOrderNumber,
        workflowApprovalsID: newWorkflowApprovalId,
        message: null,
        approvalUserPoolsID: null,
        approvalContactTypesIDs: null,
        bReasonCode: false,
        childrenWorkflowApprovalOutcomeIDs: null,
        bNewlyCreated: true, // So we can delete the id we assign here just before we post to the endpoint
        bEmailNotifications: false,
      };

      newReferenceData = changeWorkflowBuilderReferenceData(
        newReferenceData,
        WorkflowReferenceDataNames.APPROVALS,
        null,
        null,
        "workflowApprovalsID",
        blankApproval,
        "append"
      );

      // Update approval outcome with the new approval id if there is only 1 action:
      if (!actions || (actions && actions.length === 0)) {
        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
          "workflowApprovalOutcomesID",
          itemData.workflowApprovalOutcomesID ?? null,
          "childWorkflowApprovalID",
          newWorkflowApprovalId,
          "replace"
        );

        // Update triggger:
        if (newReferenceData?.trigger && itemData.parentOrderNumber === -1) {
          newReferenceData.trigger.childWorkflowActionID = null;
          newReferenceData.trigger.childWorkflowApprovalID = newWorkflowApprovalId;
        }
      }
      // Update approval data childWorkflowActionID with last actions id

      setParsedData(newParsedData);
      setReferenceData(newReferenceData);
    }
  };

  const deleteAction = (idx: number) => {
    if (actions && Array.isArray(actions) && actions.length - 1 >= idx) {
      let newParsedData = _.cloneDeep(parsedData);

      let newReferenceData = _.cloneDeep(referenceData);

      let approvalNewParentOrderNumber = null;

      if (actions.length === 1 && idx === 0) {
        approvalNewParentOrderNumber = itemData.parentOrderNumber;

        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "workflowActionsID",
          null,
          "replace"
        );

        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "workflowActionTypesID",
          null,
          "replace"
        );

        // Delete action ID from approval outcome
        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
          "workflowApprovalOutcomesID",
          itemData.workflowApprovalOutcomesID ?? null,
          "childWorkflowActionID",
          null,
          "replace"
        );

        // Delete node first action id:
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "nodeFirstActionId",
          null,
          "replace"
        );

        // Add approval ID as child approval id for outcome:
        if (itemData && itemData.nodeApprovalId) {
          newReferenceData = changeWorkflowBuilderReferenceData(
            newReferenceData,
            WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
            "workflowApprovalOutcomesID",
            itemData.workflowApprovalOutcomesID ?? null,
            "childWorkflowApprovalID",
            itemData.nodeApprovalId,
            "replace"
          );
        }

        // Clear order number for node unless it's for the first node containing the trigger
        if (itemData && !itemData.nodeApprovalId && itemData.parentOrderNumber !== -1) {
          newParsedData = updateWorkflowBuilderValue(
            newParsedData,
            `${uniqueId}`,
            "orderNumber",
            null,
            "replace"
          );
        }

        // Update trigger:
        if (newReferenceData?.trigger && itemData.parentOrderNumber === -1) {
          newReferenceData.trigger.childWorkflowActionID = null;
          newReferenceData.trigger.childWorkflowApprovalID = itemData.nodeApprovalId ?? null;
        }
      } else if (actions.length > 1 && idx === 0) {
        const newFirstActionId = actions[1].workflowActionsID;
        const newFirstActionTypesId = actions[1].workflowActionTypesID;
        const oldFirstActionId = actions[0].workflowActionsID;
        let newFirstActionParentOrderNumber = actions[0].parentOrderNumber;

        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "workflowActionsID",
          newFirstActionId ?? null,
          "replace"
        );

        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "nodeFirstActionId",
          newFirstActionId ?? null,
          "replace"
        );

        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "workflowActionTypesID",
          newFirstActionTypesId ?? null,
          "replace"
        );

        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.ACTIONS,
          "workflowActionsID",
          newFirstActionId ?? null,
          "parentOrderNumber",
          newFirstActionParentOrderNumber,
          "replace"
        );

        // Change action ID for approval outcome
        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
          "workflowApprovalOutcomesID",
          itemData.workflowApprovalOutcomesID ?? null,
          "childWorkflowActionID",
          newFirstActionTypesId,
          "replace"
        );

        // Update trigger:
        if (newReferenceData?.trigger && itemData.parentOrderNumber === -1) {
          newReferenceData.trigger.childWorkflowActionID = newFirstActionTypesId;
          newReferenceData.trigger.childWorkflowApprovalID = null;
        }
      } else if (idx > 0 && actions.length - 1 === idx) {
        approvalNewParentOrderNumber = actions[idx - 1].orderNumber;
      } else if (actions.length > 1 && idx > 0 && actions.length - 1 > idx) {
        // Don't need to update approval parent order number as it's not the last action
        // Do need to update the next action's parent order number to the previous action's order number
        const previousActionOrderNumber = actions[idx - 1].orderNumber;
        const nextActionId = actions[idx + 1].workflowActionsID;

        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.ACTIONS,
          "workflowActionsID",
          nextActionId ?? null,
          "parentOrderNumber",
          previousActionOrderNumber,
          "replace"
        );
      }

      if (itemData.nodeApprovalId && typeof approvalNewParentOrderNumber === "number") {
        newReferenceData = changeWorkflowBuilderReferenceData(
          newReferenceData,
          WorkflowReferenceDataNames.APPROVALS,
          "workflowApprovalsID",
          itemData.nodeApprovalId ?? null,

          "parentOrderNumber",
          approvalNewParentOrderNumber !== -1 ? approvalNewParentOrderNumber : 0, // 0 for trigger
          "replace"
        );
      }

      const actionIdToDelete = actions[idx].workflowActionsID;

      // Delete action from reference data:
      newReferenceData = changeWorkflowBuilderReferenceData(
        newReferenceData,
        WorkflowReferenceDataNames.ACTIONS,
        "workflowActionsID",
        actionIdToDelete ?? null,
        "",
        null,
        "delete"
      );

      setParsedData(newParsedData);
      setReferenceData(newReferenceData);
    }
  };

  const deleteApproval = () => {
    if (itemData && itemData.nodeApprovalId) {
      deleteApprovalOutcomes(
        itemData.nodeApprovalId,

        itemData.childrenWorkflowApprovalOutcomeIDs,
        true
      );
    }
  };

  const handleSaveChangeNodeType = () => {
    if (nodeType === 2) {
      // Approval
      addApproval();
      setShowNodeTypeSelector(false);
      setShowAddNewElement(false);
    } else {
      addAction();
      setShowNodeTypeSelector(false);
    }

    setNodeType(null);
  };

  const handleAddNewElement = () => {
    setShowAddNewElement(true);

    setShowNodeTypeSelector(true);
  };

  const getChildElementIdsToDelete = (
    childElements: any[],
    idsToDelete: ChildElementIdsToDelete
  ) => {
    let tempIdsToDelete = {
      ...idsToDelete,
    };

    childElements.forEach((childElement) => {
      // Action ids:
      if (childElement.workflowActionsID) {
        tempIdsToDelete = {
          ...tempIdsToDelete,
          actionIds: [...tempIdsToDelete.actionIds, childElement.workflowActionsID],
        };
      }
      // Approval ids:
      if (childElement.workflowApprovalsID) {
        tempIdsToDelete = {
          ...tempIdsToDelete,
          approvalIds: [...tempIdsToDelete.approvalIds, childElement.workflowApprovalsID],
        };
      }

      // Child approval ids:
      if (childElement.childWorkflowApprovalID) {
        tempIdsToDelete = {
          ...tempIdsToDelete,
          approvalIds: [...tempIdsToDelete.approvalIds, childElement.childWorkflowApprovalID],
        };
      }

      // Approval outcome ids:
      if (childElement.workflowApprovalOutcomesID) {
        tempIdsToDelete = {
          ...tempIdsToDelete,
          approvalOutcomeIds: [
            ...tempIdsToDelete.approvalOutcomeIds,
            childElement.workflowApprovalOutcomesID,
          ],
        };
      }

      if (
        childElement.childElements &&
        Array.isArray(childElement.childElements) &&
        childElement.childElements.length > 0
      ) {
        tempIdsToDelete = getChildElementIdsToDelete(childElement.childElements, tempIdsToDelete);
      }
    });

    return tempIdsToDelete;
  };

  const deleteApprovalOutcomes = (
    approvalId: number,
    approvalOutcomeIds: number[],
    deleteAllApprovalOutcomes: boolean = false
  ) => {
    let newParsedData = _.cloneDeep(parsedData);

    let newReferenceData = _.cloneDeep(referenceData);

    const newIds =
      itemData &&
      itemData.childrenWorkflowApprovalOutcomeIDs &&
      Array.isArray(itemData.childrenWorkflowApprovalOutcomeIDs)
        ? itemData.childrenWorkflowApprovalOutcomeIDs.filter(
            (item: number) => !approvalOutcomeIds.includes(item)
          )
        : [];

    newParsedData = updateWorkflowBuilderValue(
      newParsedData,
      `${uniqueId}`,
      "childrenWorkflowApprovalOutcomeIDs",
      newIds && newIds.length > 0 ? newIds : null,
      "replace"
    );

    const newChildElements =
      itemData && itemData.childElements && Array.isArray(itemData.childElements)
        ? [...itemData.childElements].filter(
            (item) => !approvalOutcomeIds.includes(item.workflowApprovalOutcomesID)
          )
        : null;

    newParsedData = updateWorkflowBuilderValue(
      newParsedData,
      `${uniqueId}`,
      "childElements",
      newChildElements,
      "replace"
    );

    if (deleteAllApprovalOutcomes) {
      const keysToDelete = [
        "workflowApprovalsID",
        "childWorkflowActionID",
        "childWorkflowApprovalID",
        "childrenWorkflowApprovalOutcomeIDs",
        "childElements",
        "message",
      ];

      keysToDelete.forEach((key) => {
        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          key,
          null,
          "replace"
        );
      });

      // If actions exist, set child approval id for outcome to first action

      if (actions && actions.length > 0) {
        const firstAction = actions[0];

        if (firstAction.workflowActionsID) {
          newReferenceData = changeWorkflowBuilderReferenceData(
            newReferenceData,
            WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
            "workflowApprovalOutcomesID",
            itemData.workflowApprovalOutcomesID ?? null,
            "childWorkflowActionID",
            firstAction.workflowActionsID,
            "replace"
          );
        }
      } else if (itemData.parentOrderNumber === -1) {
        // Set order number for first node as the orderNumber of the trigger

        newParsedData = updateWorkflowBuilderValue(
          newParsedData,
          `${uniqueId}`,
          "orderNumber",
          0,
          "replace"
        );
      } else {
        // Set order number for the node (if it's not the first node) to its approval outcome's order number

        if (
          itemData.workflowApprovalOutcomesID &&
          referenceData &&
          referenceData.approvalOutcomes &&
          Array.isArray(referenceData.approvalOutcomes) &&
          referenceData.approvalOutcomes.length > 0
        ) {
          const approvalOutcomeOrderNumber = referenceData.approvalOutcomes.filter(
            (item: any) => item.workflowApprovalOutcomesID === itemData.workflowApprovalOutcomesID
          );

          if (
            approvalOutcomeOrderNumber.length === 1 &&
            typeof approvalOutcomeOrderNumber[0] === "object" &&
            approvalOutcomeOrderNumber[0].hasOwnProperty("orderNumber")
          ) {
            const newOrderNumber = approvalOutcomeOrderNumber[0].orderNumber;

            newParsedData = updateWorkflowBuilderValue(
              newParsedData,
              `${uniqueId}`,
              "orderNumber",
              newOrderNumber,
              "replace"
            );
          }
        }
      }
    }

    const childElementsToDelete =
      itemData && itemData.childElements && Array.isArray(itemData.childElements)
        ? [...itemData.childElements].filter((item) =>
            approvalOutcomeIds.includes(item.workflowApprovalOutcomesID)
          )
        : null;

    let idsToDelete: ChildElementIdsToDelete | null = {
      actionIds: [],
      approvalIds: [],
      approvalOutcomeIds: [],
    };

    idsToDelete = childElementsToDelete
      ? getChildElementIdsToDelete(childElementsToDelete, idsToDelete)
      : null;

    // Not all action information is stored in parsedData, need to check parentOrderNumbers to see if they are related to other actions
    let extraActionIdsToDelete: number[] = [];

    if (idsToDelete && idsToDelete.actionIds.length > 0) {
      idsToDelete.actionIds.forEach((actionId) => {
        extraActionIdsToDelete = getExtraActionIdsToDelete(
          referenceData,
          actionId,
          "workflowActionsID",
          extraActionIdsToDelete
        );
      });
    }
    if (idsToDelete) {
      idsToDelete.actionIds = [...new Set([...idsToDelete.actionIds, ...extraActionIdsToDelete])];
      idsToDelete.approvalIds = [...new Set([...idsToDelete.approvalIds])];
      idsToDelete.approvalOutcomeIds = [...new Set([...idsToDelete.approvalOutcomeIds])];

      if (idsToDelete.actionIds && idsToDelete.actionIds.length > 0) {
        idsToDelete.actionIds.forEach((id) => {
          newReferenceData = changeWorkflowBuilderReferenceData(
            newReferenceData,
            WorkflowReferenceDataNames.ACTIONS,
            "workflowActionsID",
            id ?? null,
            "",
            null,
            "delete"
          );
        });
      }

      if (idsToDelete.approvalIds && idsToDelete.approvalIds.length > 0) {
        idsToDelete.approvalIds.forEach((id) => {
          newReferenceData = changeWorkflowBuilderReferenceData(
            newReferenceData,
            WorkflowReferenceDataNames.APPROVALS,
            "workflowApprovalsID",
            id ?? null,
            "",
            null,
            "delete"
          );
        });
      }

      if (idsToDelete.approvalOutcomeIds && idsToDelete.approvalOutcomeIds.length > 0) {
        idsToDelete.approvalOutcomeIds.forEach((id) => {
          newReferenceData = changeWorkflowBuilderReferenceData(
            newReferenceData,
            WorkflowReferenceDataNames.APPROVAL_OUTCOMES,
            "workflowApprovalOutcomesID",
            id ?? null,
            "",
            null,
            "delete"
          );
        });
      }
    }

    // Remove outcome Ids from approval childrenWorkflowApprovalOutcomeIds:
    newReferenceData = changeWorkflowBuilderReferenceData(
      newReferenceData,
      WorkflowReferenceDataNames.APPROVALS,
      "workflowApprovalsID",
      approvalId ?? null,
      "childrenWorkflowApprovalOutcomeIDs",
      newIds && newIds.length > 0 ? newIds : null,
      "replace"
    );

    if (itemData && itemData.nodeApprovalId && deleteAllApprovalOutcomes) {
      newParsedData = updateWorkflowBuilderValue(
        newParsedData,
        `${uniqueId}`,
        "nodeApprovalId",
        null,
        "replace"
      );

      newReferenceData = changeWorkflowBuilderReferenceData(
        newReferenceData,
        WorkflowReferenceDataNames.APPROVALS,
        "workflowApprovalsID",
        itemData.nodeApprovalId ?? null,
        "",
        null,
        "delete"
      );

      // Update trigger:
      if (itemData.parentOrderNumber === -1 && newReferenceData?.trigger) {
        newReferenceData.trigger.childWorkflowApprovalID = null;
      }
    }
    setReferenceData(newReferenceData);
    setParsedData(newParsedData);
  };

  let topBorderType:
    | "TopBorderNone"
    | "TopBorderFull"
    | "TopBorderHalfRight"
    | "TopBorderHalfLeft" = "TopBorderFull";

  // if (parentOrderNumber === 0) {
  if (parentOrderNumber === -1) {
    topBorderType = "TopBorderNone";
  } else if (totalNumberofChildElements > 1 && childElementIndex === 0) {
    topBorderType = "TopBorderHalfRight";
  } else if (
    totalNumberofChildElements > 1 &&
    childElementIndex === totalNumberofChildElements - 1
  ) {
    topBorderType = "TopBorderHalfLeft";
  } else if (totalNumberofChildElements > 1) {
    topBorderType = "TopBorderFull";
  } else {
    topBorderType = "TopBorderNone";
  }

  useEffect(() => {
    if (
      itemData &&
      itemData.workflowApprovalOutcomesID &&
      workflowBuilderErrors &&
      workflowBuilderErrors.approvalOutcomes &&
      workflowBuilderErrors.approvalOutcomes.hasOwnProperty(itemData.workflowApprovalOutcomesID)
    ) {
      const tempApprovalOutcomeErrors: any =
        workflowBuilderErrors.approvalOutcomes[
          itemData.workflowApprovalOutcomesID as keyof typeof workflowBuilderErrors.approvalOutcomes
        ];

      setApprovalOutcomeErrors(tempApprovalOutcomeErrors);
    } else {
      setApprovalOutcomeErrors(null);
    }
  }, [itemData, workflowBuilderErrors]);

  let middleElement = false;
  if (parentChildElementsLength % 2 !== 0) {
    if (Math.floor(parentChildElementsLength / 2) === childElementIndex) {
      middleElement = true;
    }
  }

  return (
    <div className={`WorkflowItem ${parentOrderNumber === -1 ? "FirstWorkflowItem" : ""}`}>
      {parentChildElementsLength > 0 && parentOrderNumber !== -1 && (
        <div className="TopVerticalNodeLine"></div>
      )}

      <div className={`TopHorizontalLine ${topBorderType}`}></div>

      <div className={`Details ${parentOrderNumber === -1 ? "FirstDetails" : ""}`}>
        {parentOrderNumber !== -1 && (
          <div
            className={`OutcomeName ${
              approvalOutcomeErrors && approvalOutcomeErrors.length > 0 ? "Error" : ""
            } 
            ${childElementIndex === 0 && parentChildElementsLength > 1 ? "Left" : ""}
            ${
              childElementIndex === parentChildElementsLength - 1 && parentChildElementsLength > 1
                ? "Right"
                : ""
            }
            ${middleElement ? "Middle" : ""}
            `}
          >
            {`${
              itemData.workflowApprovalOutcomesName &&
              typeof itemData.workflowApprovalOutcomesName === "string" &&
              itemData.workflowApprovalOutcomesName.trim() !== ""
                ? itemData.workflowApprovalOutcomesName
                : "No name"
            }`}
          </div>
        )}

        {approvalOutcomeErrors && approvalOutcomeErrors.length && (
          <div className="ApprovalOutcomeErrors">
            {approvalOutcomeErrors.map((item) => {
              return <div className="Error">{`${item}`}</div>;
            })}
          </div>
        )}

        {/* Triggers: */}
        {parentOrderNumber === -1 && (
          <>
            {showTriggerTypeSelector && (
              <TriggerTypeSelector
                key={`workflow-item-trigger-type-selector-${`${uniqueId}`}`}
                parentOrderNumber={parentOrderNumber}
                orderNumber={orderNumber}
                fieldName="test"
                triggerOptions={triggerOptions}
                type="trigger"
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                showTriggerTypeSelector={showTriggerTypeSelector}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
                setWorkflowBuilderErrors={setWorkflowBuilderErrors}
                setTempTriggerMilestoneData={setTempTriggerMilestoneData}
              />
            )}

            {triggerTypeLabel && triggerTypeLabel === "document upload" && (
              <DocumentUploadTrigger
                key={`workflow-item-document-upload-trigger-${`${uniqueId}`}`}
                fieldName="test"
                documentOptions={documentOptions}
                projectOptions={projectOptions}
                type="trigger"
                triggerData={itemData}
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                showTriggerTypeSelector={showTriggerTypeSelector}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
              />
            )}

            {triggerTypeLabel && triggerTypeLabel === "document request" && (
              <DocumentRequestTrigger
                key={`workflow-item-document-request-trigger-${`${uniqueId}`}`}
                fieldName="test"
                documentOptions={documentOptions}
                projectOptions={projectOptions}
                type="trigger"
                triggerData={itemData}
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                contactTypes={contactTypes}
                userPools={userPools}
                showTriggerTypeSelector={showTriggerTypeSelector}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
              />
            )}

            {triggerTypeLabel && triggerTypeLabel === "milestone update" && (
              <MilestoneUpdateTrigger
                key={`workflow-item-milestone-update-trigger-${`${uniqueId}`}`}
                fieldName="test"
                selectOptions={triggerOptions}
                projectOptions={projectOptions}
                triggerDatesOptions={triggerDatesOptions}
                type="trigger"
                parentOrderNumber={parentOrderNumber}
                orderNumber={orderNumber}
                // triggerData={itemData}
                triggerData={referenceData?.trigger}
                // triggerData={itemData}
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                uniqueId={uniqueId}
                tempTriggerMilestoneId={tempTriggerMilestoneId}
                setTempTriggerMilestoneId={setTempTriggerMilestoneId}
                tempTriggerMilestoneData={tempTriggerMilestoneData}
                setTempTriggerMilestoneData={setTempTriggerMilestoneData}
                showTriggerTypeSelector={showTriggerTypeSelector}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
                milestonestasksList={milestonestasksList}
                unParsedTasksAndMilestones={unParsedTasksAndMilestones}
              />
            )}

            {triggerTypeLabel && triggerTypeLabel === "submitted survey" && (
              <SubmittedSurveyTrigger
                key={`workflow-item-milestone-update-trigger-${`${uniqueId}`}`}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
              />
            )}

            {triggerTypeLabel && triggerTypeLabel === "dispatched survey" && (
              <DispatchedSurveyTrigger
                key={`workflow-item-dispatched-survey-trigger-${`${uniqueId}`}`}
                surveyTemplateOptions={surveyTemplateOptions}
                projectOptions={projectOptions}
                triggerData={itemData}
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                showTriggerTypeSelector={showTriggerTypeSelector}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
              />
            )}

            {triggerTypeLabel && triggerTypeLabel === "new communication" && (
              <NewCommunicationTrigger
                key={`workflow-item-new-communication-trigger-${`${uniqueId}`}`}
                fieldName="test"
                documentOptions={documentOptions}
                projectOptions={projectOptions}
                type="trigger"
                triggerData={itemData}
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                showTriggerTypeSelector={showTriggerTypeSelector}
                setShowTriggerTypeSelector={setShowTriggerTypeSelector}
                workflowBuilderErrors={workflowBuilderErrors}
                contactTypes={contactTypes}
              />
            )}
          </>
        )}

        {/* Actions: */}

        {actions &&
          actions.map((item: any, idx: number) => {
            return (
              <Action
                key={`workflow-item-action-${`${uniqueId}`}-${idx}`}
                index={idx}
                actionData={actions}
                parsedData={parsedData}
                setParsedData={setParsedData}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                actionTypes={actionTypes}
                uniqueId={uniqueId}
                tempActionMilestoneId={tempActionMilestoneId}
                setTempActionMilestoneId={setTempActionMilestoneId}
                tempActionMilestoneData={tempActionMilestoneData}
                setTempActionMilestoneData={setTempActionMilestoneData}
                tempActionDestinationMilestoneId={tempActionDestinationMilestoneId}
                setTempActionDestinationMilestoneId={setTempActionDestinationMilestoneId}
                milestonestasksList={milestonestasksList}
                setMilestonestasksList={setMilestonestasksList}
                contactTypes={contactTypes}
                userPools={userPools}
                deleteAction={deleteAction}
                workflowBuilderErrors={workflowBuilderErrors}
                setWorkflowBuilderErrors={setWorkflowBuilderErrors}
                documentOptions={documentOptions}
                triggerDatesOptions={triggerDatesOptions}
                workflows={workflows}
              />
            );
          })}

        {showNodeTypeSelector && (
          <div className="NodeTypeSelectorContainer">
            {/* Select between an action and an approval */}

            <div className="NodeTypeSelectorTopVerticalNodeLine"></div>

            <div className="Row">
              <div className="Label">New element type</div>

              <Dropdown
                onChange={handleChangeNodeType}
                value={
                  nodeType
                    ? nodeTypes.find((item) => {
                        nodeType === item.value;
                      })
                    : null
                }
                options={nodeTypes}
                placeholder="Please select"
                roundedRectangle
                portalMode={true}
              />
            </div>

            <button
              type="button"
              className={`SaveBtn ${!nodeType ? "Disabled" : ""}`}
              onClick={handleSaveChangeNodeType}
            >
              Save
            </button>
          </div>
        )}

        <div className="AddNewNodeContainer Btn" onClick={handleAddNewElement}>
          <div className="AddNewElementTopVerticalNodeLine"></div>

          <div className="Text">Add new element</div>

          <div className="AddIconLineOne"></div>
          <div className="AddIconLineTwo"></div>

          {itemData.nodeApprovalId && <div className="AddNewElementBottomVerticalNodeLine"></div>}
        </div>

        {/* Approvals: */}

        {/* Approval decisions: */}

        {showApprovalEditBox && (
          <EditApprovalDecision
            showApprovalEditBox={showApprovalEditBox}
            setShowApprovalEditBox={setShowApprovalEditBox}
            deleteApproval={deleteApproval}
          />
        )}

        {itemData.nodeApprovalId && (
          <ApprovalDecision
            key={`workflow-item-approval-decision-${`${uniqueId}`}`}
            fieldName="test"
            selectOptions={triggerOptions}
            type="trigger"
            itemData={itemData}
            parsedData={parsedData}
            setParsedData={setParsedData}
            referenceData={referenceData}
            setReferenceData={setReferenceData}
            uniqueId={uniqueId}
            contactTypes={contactTypes}
            userPools={userPools}
            deleteApprovalOutcomes={deleteApprovalOutcomes}
            workflowBuilderErrors={workflowBuilderErrors}
            showApprovalEditBox={showApprovalEditBox}
            setShowApprovalEditBox={setShowApprovalEditBox}
          />
        )}

        {childElements && childElements.length > 0 && (
          <div className="BottomVerticalNodeLine"></div>
        )}
      </div>

      {childElements && (
        <div className="ChildElements">
          {childElements.map((item, idx) => {
            return (
              <WorkflowItem
                key={`workflow-item-${`${uniqueId}, ${idx}`}`}
                parentOrderNumbers={`${uniqueId}, ${idx}`}
                uniqueId={`${uniqueId}, ${idx}`}
                parentOrderNumber={item.parentOrderNumber}
                orderNumber={item.orderNumber}
                childElements={item.childElements}
                triggerOptions={triggerOptions}
                documentOptions={documentOptions}
                projectOptions={projectOptions}
                triggerDatesOptions={triggerDatesOptions}
                parsedData={parsedData}
                setParsedData={setParsedData}
                itemData={item}
                referenceData={referenceData}
                setReferenceData={setReferenceData}
                parentChildElementsLength={childElements.length}
                contactTypes={contactTypes}
                userPools={userPools}
                actionTypes={actionTypes}
                tempTriggerMilestoneId={tempTriggerMilestoneId}
                setTempTriggerMilestoneId={setTempTriggerMilestoneId}
                tempTriggerMilestoneData={tempTriggerMilestoneData}
                setTempTriggerMilestoneData={setTempTriggerMilestoneData}
                tempActionMilestoneId={tempActionMilestoneId}
                setTempActionMilestoneId={setTempActionMilestoneId}
                tempActionMilestoneData={tempActionMilestoneData}
                setTempActionMilestoneData={setTempActionMilestoneData}
                tempActionDestinationMilestoneId={tempActionDestinationMilestoneId}
                setTempActionDestinationMilestoneId={setTempActionDestinationMilestoneId}
                milestonestasksList={milestonestasksList}
                setMilestonestasksList={setMilestonestasksList}
                workflowBuilderErrors={workflowBuilderErrors}
                setWorkflowBuilderErrors={setWorkflowBuilderErrors}
                childElementIndex={idx}
                totalNumberofChildElements={childElements.length}
                unParsedTasksAndMilestones={unParsedTasksAndMilestones}
                workflows={workflows}
                surveyTemplateOptions={surveyTemplateOptions}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkflowItem;
